import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import {
  buildComplexQueryWithCrossFilters,
  buildQuery,
} from '~/features/advanced-search/utils/build-query';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useTableSettings } from '~/hooks/useTableSettings';
import { api } from '~/redux/api/createApi';
import { Dashboard } from '~/redux/api/methods/dashboardV2';
import { slices } from '~/redux/slices';
import {
  DEFAULT_TABLE_SETTINGS,
  DEFAULT_TABLE_SORT_COLUMN_PRE_SIGN,
} from '~/utils/table';
import { testIsAdmin } from '~/utils/user';

import { DashboardV2Charts } from '../DashboardV2/DashboardV2Charts/DashboardV2Charts';
import { EditCharts } from '../DashboardV2/EditCharts/EditCharts';
import SaveDashboard from '../DashboardV2/SaveDashboard/SaveDashboard';
import { DashboardV3Filters } from './DashboardV3Filters/DashboardV3Filters';
import { DashboardV3SearchResults } from './DashboardV3SearchResults/DashboardV3SearchResults';
import { FIRST_COLUMN_PREFIX, getFirstColumnKey } from './utils';

type Props = {
  dashboard: Dashboard;
};

export const DashboardV3 = ({ dashboard }: Props) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const user = useCurrentUser();
  const intl = useIntl();

  //Redux Selectors
  const page = useSelector(slices.dashboardV2.selectors.selectPage);
  const pageSize = useSelector(slices.dashboardV2.selectors.selectPageSize);
  const sortBy = useSelector(slices.dashboardV2.selectors.selectSortBy);
  const columns = useSelector(slices.dashboardV2.selectors.selectColumnOrder);
  const activeDashboard = useSelector(
    slices.dashboardV2.selectors.selectActiveDashboard,
  );

  //Query Selectors
  const searchText = useSelector(slices.dashboardV2.selectors.selectSearchText);
  const activeFilters = useSelector(
    slices.dashboardV2.selectors.selectActiveFilters,
  );
  const crossFilters = useSelector(
    slices.dashboardV2.selectors.selectCrossFilters,
  );
  const intervals = useSelector(slices.dashboardV2.selectors.selectIntervals);
  const chartWidths = useSelector(
    slices.dashboardV2.selectors.selectChartWidths,
  );
  const selectedFilters = useSelector(
    slices.dashboardV2.selectors.selectSelectedFilters,
  );
  const hasDataChanges = useSelector(
    slices.dashboardV2.selectors.selectDashboardDirty,
  );
  const queryBuilder = useSelector(
    slices.dashboardV2.selectors.selectQueryBuilder,
  );
  const canSaveDashboard = testIsAdmin(user) || dashboard.creatorId === user.id;
  //Charts
  const charts = useSelector(slices.dashboardV2.selectors.selectCharts);

  const isCurrentDashboardActive = activeDashboard.id === dashboard.id;

  //RTQK
  const {
    data: searchFilters,
    isError: isGetFilterError,
    isFetching: isFetchingFilters,
  } = api.endpoints.getFiltersV2.useQuery({
    type: dashboard.entity ?? 'ticket',
  });

  const query = useMemo(() => {
    if (queryBuilder) {
      return buildComplexQueryWithCrossFilters(
        queryBuilder,
        crossFilters,
        searchFilters ?? {},
      );
    }
    if (activeFilters?.length || crossFilters?.length || searchFilters) {
      return buildQuery({
        booleanQuery: searchText,
        crossFilters,
        filters: activeFilters,
        searchFilters,
      });
    } else {
      return [];
    }
  }, [activeFilters, crossFilters, searchFilters, searchText, queryBuilder]);

  const {
    tableSettings = {
      ...DEFAULT_TABLE_SETTINGS,
      columnOrder: dashboard.columns || [],
      sortBy: DEFAULT_TABLE_SORT_COLUMN_PRE_SIGN,
    },
    updateTableSettings,
  } = useTableSettings(`DASHBOARD:${dashboard.id}`);

  const queryToSave = useMemo(() => {
    if (queryBuilder && searchFilters) {
      return queryBuilder;
    } else if (activeFilters?.length || searchFilters) {
      return buildQuery({
        booleanQuery: searchText,
        crossFilters: [],
        filters: activeFilters,
        searchFilters,
      });
    } else {
      return [];
    }
  }, [activeFilters, crossFilters, queryBuilder, searchFilters, searchText]);

  const dashboardData = useMemo(
    () => ({
      charts: charts.map((item, index) => ({
        filter_id: item.id,
        sorted_index: index,
        interval: intervals[item.id],
        width: chartWidths[item.id] ? chartWidths[item.id] : undefined,
        type: item.type,
      })),
      columns: columns || [],
      filters: selectedFilters
        .filter((f) => !f.values.length)
        .map((filter) => filter.fieldId.toString()),
      id: dashboard.id,
      name: dashboard.name,
      entity: dashboard.entity,
      query,
      is_default: !!dashboard.is_default,
      creatorId: dashboard.creatorId,
    }),
    [
      dashboard,
      charts,
      columns,
      selectedFilters,
      intervals,
      chartWidths,
      query,
    ],
  );

  const firstColumn = useMemo(() => {
    if (searchFilters) {
      return (
        Object.entries(searchFilters).find(([key]) =>
          key.startsWith(FIRST_COLUMN_PREFIX),
        )?.[0] ?? ''
      );
    }
    return '';
  }, [searchFilters]);

  // useEffects
  useEffect(() => {
    if (searchFilters) {
      const defaultSortBy = getFirstColumnKey(dashboard.entity ?? '');
      dispatch(
        slices.dashboardV2.actions.initDashboard({
          dashboard,
          searchFilters,
          tableSettings,
          defaultSortBy: { id: defaultSortBy, desc: false },
        }),
      );
    }
  }, [searchFilters, dashboard]);

  const availableFilters =
    columns.indexOf(firstColumn) >= 0
      ? [...columns]
      : [firstColumn, ...columns];

  const searchParams = {
    page,
    pageSize,
    sortBy: { ...sortBy },
    type: dashboard.entity ?? 'ticket',
    query,
    filters: availableFilters,
  };
  const {
    data: searchResponse,
    isLoading: isLoadingSearch,
    isFetching: isSearchFetching,
    isError: isSearchError,
  } = api.endpoints.search.useQuery(searchParams, {
    skip: !isCurrentDashboardActive,
  });

  const isLoading = isFetchingFilters || isLoadingSearch;

  useEffect(() => {
    if (isSearchError || isGetFilterError) {
      toast({
        message: intl.formatMessage({
          id: 'DashboardV3.fetchDataError',
          defaultMessage:
            'An error occurred when fetching data for this dashboard.',
          description: 'Error message when dashboard data fails to load',
        }),
        status: 'danger',
      });
    }
  }, [isSearchError, isGetFilterError]);

  const onSaveSuccess = () => {
    updateTableSettings({
      ...tableSettings,
      columnOrder: columns,
    });
    dispatch(slices.dashboardV2.actions.setDashboardDirty(false));
  };

  const loadingContent = {
    isLoading: !isCurrentDashboardActive,
    message: intl.formatMessage({
      id: 'DashboardV3.loadingMessage',
      defaultMessage: 'Loading Dashboard...',
      description: 'Loading message for dashboard',
    }),
  };
  const loadingTableContent = { isLoading };

  return (
    <ContentContainer loadingContent={loadingContent}>
      <>
        <Layout align="center" justify="space-between" spacing={8}>
          <Text as="h1" m={0} variant="title">
            {dashboard.name}
          </Text>

          <Layout align="center" spacing={4}>
            <EditCharts entity={dashboard.entity} />
            <SaveDashboard
              dashboardData={dashboardData}
              query={queryToSave}
              isDashboardDirty={hasDataChanges}
              hasSavePermission={canSaveDashboard}
              onSaveSucess={onSaveSuccess}
            />
          </Layout>
        </Layout>

        <DashboardV3Filters entityType={dashboard.entity} />
        <DashboardV2Charts
          charts={charts}
          queryParams={{
            booleanQuery: searchText,
            filters: activeFilters,
            crossFilters,
            searchFilters,
            queryBuilder: undefined,
          }}
          entity={dashboard.entity}
          searchParams={searchParams}
        />
        {!isSearchError && (
          <ContentContainer loadingContent={loadingTableContent}>
            <DashboardV3SearchResults
              dashboard={dashboard}
              searchFilters={searchFilters || {}}
              isLoading={isSearchFetching}
              data={searchResponse}
              query={query}
            />
          </ContentContainer>
        )}
      </>
    </ContentContainer>
  );
};
